import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials = [
    {
      id: 1,
      name: "Raj S.",
      role: "Owner Operator",
      location: "Toronto, ON",
      image: "https://images.unsplash.com/photo-1626712211690-8de4fe30177c?crop=entropy&cs=srgb&fm=jpg&w=200&q=80",
      quote: "MyLane found better loads than my old dispatcher and saved me thousands every month. I was skeptical at first, but the numbers don't lie.",
      rating: 5,
      savings: "$3,200/mo",
    },
    {
      id: 2,
      name: "Alex M.",
      role: "Flatbed Driver",
      location: "Chicago, IL",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&w=200&q=80",
      quote: "This is the easiest dispatch service I've used. Everything happens on WhatsApp — no complicated apps or phone tag. I can find loads while getting coffee.",
      rating: 5,
      savings: "$2,800/mo",
    },
    {
      id: 3,
      name: "Mike T.",
      role: "Reefer Driver",
      location: "Dallas, TX",
      image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?crop=entropy&cs=srgb&fm=jpg&w=200&q=80",
      quote: "Finally, a dispatch service that doesn't take 10% of my hard-earned money. MyLane gets me better rates and I keep more in my pocket.",
      rating: 5,
      savings: "$3,500/mo",
    },
    {
      id: 4,
      name: "Sarah K.",
      role: "Dry Van Driver",
      location: "Vancouver, BC",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&w=200&q=80",
      quote: "I was paying $2,500/month to my dispatcher. Now I pay $297 and get better service. The backhaul optimization alone is worth it.",
      rating: 5,
      savings: "$2,600/mo",
    },
  ];

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section
      id="testimonials"
      className="py-20 md:py-32 bg-slate-50"
      data-testid="testimonials-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider mb-4">
            Testimonials
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-primary tracking-tight uppercase mb-6">
            What Drivers Say
          </h2>
          <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto">
            Join hundreds of owner-operators who are keeping more of their earnings.
          </p>
        </motion.div>

        {/* Featured Testimonial */}
        <div className="relative mb-12">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.4 }}
              className="bg-white rounded-2xl p-8 md:p-12 shadow-lg"
              data-testid="featured-testimonial"
            >
              <div className="grid md:grid-cols-3 gap-8 items-center">
                {/* Avatar and Info */}
                <div className="text-center md:text-left">
                  <div className="relative inline-block mb-4">
                    <img
                      src={testimonials[currentIndex].image}
                      alt={testimonials[currentIndex].name}
                      className="w-24 h-24 rounded-full object-cover border-4 border-accent"
                    />
                    <div className="absolute -bottom-2 -right-2 bg-accent text-white text-xs font-bold px-3 py-1 rounded-full">
                      {testimonials[currentIndex].savings}
                    </div>
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-primary">
                    {testimonials[currentIndex].name}
                  </h3>
                  <p className="text-slate-600">{testimonials[currentIndex].role}</p>
                  <p className="text-slate-500 text-sm">{testimonials[currentIndex].location}</p>
                  <div className="flex justify-center md:justify-start gap-1 mt-3">
                    {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-accent text-accent" />
                    ))}
                  </div>
                </div>

                {/* Quote */}
                <div className="md:col-span-2 relative">
                  <Quote className="absolute -top-4 -left-4 w-12 h-12 text-accent/20" />
                  <p className="text-xl md:text-2xl text-slate-700 leading-relaxed italic relative z-10">
                    "{testimonials[currentIndex].quote}"
                  </p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="flex justify-center gap-4 mt-8">
            <button
              onClick={prevTestimonial}
              className="w-12 h-12 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors"
              data-testid="testimonial-prev"
            >
              <ChevronLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div className="flex items-center gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    index === currentIndex ? "bg-accent" : "bg-slate-300"
                  }`}
                  data-testid={`testimonial-dot-${index}`}
                />
              ))}
            </div>
            <button
              onClick={nextTestimonial}
              className="w-12 h-12 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors"
              data-testid="testimonial-next"
            >
              <ChevronRight className="w-5 h-5 text-slate-600" />
            </button>
          </div>
        </div>

        {/* Mini Testimonial Cards */}
        <div className="grid md:grid-cols-4 gap-4">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              onClick={() => setCurrentIndex(index)}
              className={`bg-white p-4 rounded-xl cursor-pointer transition-all ${
                index === currentIndex
                  ? "ring-2 ring-accent shadow-md"
                  : "hover:shadow-md"
              }`}
              data-testid={`mini-testimonial-${index}`}
            >
              <div className="flex items-center gap-3">
                <img
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <p className="font-semibold text-sm text-primary">{testimonial.name}</p>
                  <p className="text-xs text-slate-500">{testimonial.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
